import type { GuideContent } from "../components/Guide";
import { shots } from "./screenshots";

const access =
	"Если нужной кнопки или раздела нет, попросите руководителя проверить ваш доступ. Меню может отличаться от учебного примера.";
export const guides: Record<string, GuideContent> = {
	start: {
		title: "Войти и начать работу",
		intro:
			"В первый раз достаточно войти в сервис и найти свою задачу. Настраивать всё самостоятельно не нужно.",
		tasks: [
			{
				title: "Как войти в сервис?",
				steps: [
					"Попросите у руководителя **рабочую ссылку, электронную почту для входа и пароль**. Ссылка на справку и ссылка на сервис — разные.",
					"Откройте рабочую ссылку в браузере — программе, в которой вы смотрите сайты. Нажмите в поле **Email** и введите выданный адрес почты.",
					"Нажмите в поле **Пароль** и введите пароль. Точки вместо букв — это нормально. Значок глаза справа позволяет проверить введённое.",
					"Нажмите **Войти** один раз и дождитесь открытия главной страницы.",
				],
				result: "вы видите приветствие и меню сервиса.",
				tip: "Нет данных для входа? Обратитесь к руководителю. Самостоятельная регистрация в текущем сервисе недоступна.",
				image: shots.login,
			},
			{
				title: "Что делать после входа?",
				steps: [
					"Посмотрите на меню слева. Если видны только значки, нажмите кнопку **разворачивания меню в левом верхнем углу**.",
					"Для работ откройте **График** или **Просмотр графика**. Для материалов — **Склад**. Выбирайте тот раздел, с которым поручили работать.",
					"В графике проверьте даты сверху и найдите свою бригаду слева. В складе откройте вкладку **Материалы**.",
				],
				result: "открыт нужный рабочий раздел.",
				tip: "Не все разделы доступны каждому сотруднику. Отсутствие кнопки не означает, что вы сделали что-то неправильно.",
			},
			{
				title: "Как пользоваться этой справкой?",
				steps: [
					"Выберите тему в меню справки. На телефоне нажмите **Меню справки**.",
					"Нажмите на нужный вопрос, чтобы открыть шаги. Обычно **«нажмите» означает один щелчок левой кнопкой мыши** или одно касание экрана.",
					"Под снимком выбирайте пронумерованные пояснения. Кнопка **Увеличить снимок** помогает прочитать мелкие надписи.",
				],
				result: "вы можете выполнить одну задачу, не читая всю справку.",
				tip: "Слова, выделенные жирным, помогают найти нужную кнопку или поле. Данные на снимках учебные; у вас будут свои объекты и бригады.",
			},
			{
				title: "Как выйти на общем компьютере?",
				steps: [
					"Нажмите круглый значок человека **справа вверху**.",
					"В открывшемся меню выберите **Выйти**.",
				],
				result:
					"снова появился экран входа. Следующий сотрудник сможет войти под своей учётной записью.",
			},
		],
		related: [
			["/schedule", "Посмотреть график"],
			["/troubleshooting", "Не получается войти"],
		],
	},
	schedule: {
		title: "Найти и назначить работу",
		intro:
			"График показывает, какая бригада работает на объекте и в какие дни. Цветная полоска в таблице называется блоком работы.",
		access,
		tasks: [
			{
				title: "Как посмотреть работу на нужный день?",
				steps: [
					"В меню сервиса откройте **График**. Если доступен только просмотр, пункт называется **Просмотр графика**.",
					"Вверху проверьте **Начальную дату** и **Конечную дату**. Нажмите на значок календаря в поле и выберите нужный день. Конечная дата должна быть не раньше начальной.",
					"Найдите бригаду **слева**, а нужный день — **сверху**. На их пересечении видны назначенные работы.",
				],
				result: "вы знаете название работы, бригаду и дни её выполнения.",
				tip: "Если нужный день за краем таблицы, прокрутите таблицу вбок. Пустая ячейка означает, что в ней не показан блок работы; это не отметка о выполнении.",
				image: shots.schedule,
			},
			{
				title: "Как назначить новую работу?",
				steps: [
					"Откройте **График** и нажмите **Создать** сверху слева.",
					"Укажите **Дату начала**. В поле **Бригада** нажмите стрелку и выберите исполнителей из списка.",
					"В поле **Объект** начните вводить название и нажмите на подходящий вариант. В **Названии блока** напишите, что нужно сделать, например «Монтаж перегородок».",
					"Укажите **Длительность (дней)**. Если появились поля времени, заполните и их. Проверьте данные и нажмите **Создать**.",
				],
				result:
					"в графике появился блок на выбранную дату у выбранной бригады.",
				tip: "Нет нужного объекта или бригады? Сначала добавьте их или попросите ответственного сотрудника. Если под полем появилась ошибка, исправьте её перед повторным нажатием.",
				image: shots.block,
			},
			{
				title: "Что означает «В ожидании»?",
				steps: [
					"Нажмите **В ожидании** над графиком. Откроется список ещё не назначенных работ, сгруппированных по объектам.",
					"Раскройте нужный объект. Чтобы назначить работу, зажмите цветной блок левой кнопкой мыши, перенесите к нужной бригаде и дате, затем отпустите.",
					"Проверьте, что блок появился в таблице у правильной бригады и на правильный день.",
				],
				result: "работа перенесена из ожидания в график.",
				tip: "Чтобы создать работу без даты, в форме «Создать блок» отметьте **Добавить в пул объектов**. В интерфейсе это означает добавление в ожидание.",
			},
			{
				title: "Как исправить или перенести работу?",
				steps: [
					"На компьютере нажмите на блок **правой кнопкой мыши**. В меню выберите **Изменить**.",
					"Поправьте дату, название или длительность и нажмите **Сохранить**.",
					"Чтобы сменить бригаду, перетащите блок на её строку: зажмите левую кнопку, переместите блок и отпустите. Проверьте новое положение.",
				],
				result: "блок показывает нужные данные и стоит в правильном месте.",
				tip: "Изменения при перетаскивании сохраняются сразу. Чтобы снять назначение, в меню блока выберите **В ожидание**. Разрезание блоков значком ножниц используйте только когда действительно нужно разделить работу.",
			},
		],
		related: [
			["/objects-and-blocks", "Добавить объект"],
			["/brigades", "Добавить бригаду"],
			["/settings", "Планирование по часам"],
		],
	},
	objects: {
		title: "Объекты и работы на них",
		intro:
			"Объект — место, где вы выполняете заказ, например офис. Блок — отдельная работа на нём, например монтаж перегородок.",
		access,
		tasks: [
			{
				title: "Как найти и открыть объект?",
				steps: [
					"Выберите раздел объектов в меню слева. Сейчас в сервисе он подписан **Обьекты**.",
					"Нажмите в поле **Поиск (в реальном времени)** и начните вводить название. Если ничего не найдено, сотрите запрос и проверьте общий список.",
					"Нажмите на название объекта в таблице. В карточке выберите **Информация**, **Блоки работ** или **Материалы**, если эта вкладка доступна.",
				],
				result: "открыта карточка нужного объекта.",
				image: shots.objects,
			},
			{
				title: "Как добавить объект?",
				steps: [
					"В списке объектов нажмите **добавить Объект**. Для обычного нового объекта оставьте **Без шаблона**.",
					"Заполните **Название**. Используйте понятное имя, например «Офис — монтаж перегородок». Поля со звёздочкой обязательны.",
					"При необходимости укажите **Менеджера**, **Адрес** и **Дату**. Нажмите **Сохранить**.",
				],
				result:
					"открылась карточка нового объекта. Теперь можно добавлять работы.",
				tip: "Поле «Менеджер» — текст с именем ответственного. Оно не создаёт учётную запись и не выдаёт человеку доступ.",
				image: shots.objectForm,
			},
			{
				title: "Как добавить работу внутри объекта?",
				steps: [
					"Откройте карточку объекта и вкладку **Блоки работ**.",
					"Нажмите **Создать блок**, заполните **Название** и **Длительность (дней)**. Затем нажмите **Добавить блок**.",
					"В списке работ нажмите **Не запланирован**, чтобы перейти к ожиданию в графике. Назначьте дату и бригаду по инструкции о графике.",
				],
				result:
					"у объекта есть работа; после назначения она появится в графике.",
				tip: "Создание блока внутри объекта ещё не означает назначение на дату. Если вы только просматриваете график, попросите менеджера запланировать работу.",
			},
		],
		related: [
			["/schedule", "Назначить работу в графике"],
			["/warehouse", "Материалы для объекта"],
		],
	},
	brigades: {
		title: "Добавить или найти бригаду",
		intro:
			"Бригада — группа исполнителей. Активные бригады отображаются в графике отдельными строками.",
		access,
		tasks: [
			{
				title: "Как создать бригаду?",
				steps: [
					"В меню сервиса выберите **Настройки**, затем вкладку **Бригады**.",
					"Нажмите **Создать бригаду**. Введите **Название бригады**, например «Бригада 1 — монтаж».",
					"Оставьте переключатель **Активная бригада** включённым. Нажмите **Создать**.",
				],
				result:
					"бригада появилась в списке активных и доступна для назначения работ.",
				tip: "Создание бригады не создаёт логины её сотрудникам. Учётные записи добавляют отдельно в разделе «Пользователи».",
				image: shots.brigades,
			},
			{
				title: "Почему бригады нет в графике?",
				steps: [
					"Откройте **Настройки → Бригады** и проверьте вкладку **Активные бригады**.",
					"Если бригады нет, проверьте **Архив**. Если она должна работать снова, попросите ответственного вернуть её в активные.",
					"Вернитесь в график и проверьте список бригад.",
				],
				result:
					"вы выяснили, отсутствует ли бригада в списке или была перенесена в архив.",
			},
		],
		related: [
			["/schedule", "Назначить работу бригаде"],
			["/users-and-roles", "Доступ сотрудников"],
		],
	},
	warehouse: {
		title: "Проверить и выдать материалы",
		intro:
			"Склад помогает понять, что есть в наличии, что уже закреплено за объектами и что нужно заказать.",
		access: `${access} Раздел «Склад» также должен быть включён в тариф организации.`,
		tasks: [
			{
				title: "Сколько материала можно использовать?",
				steps: [
					"Откройте **Склад → Материалы** и найдите материал. При необходимости выберите категорию в списке над таблицей.",
					"В колонке **Остатки** посмотрите три числа: **На складе**, **В резерве**, **Доступно**.",
					"Для новой работы ориентируйтесь на **Доступно**. Например, из 100 метров 20 уже зарезервированы — свободно 80 метров.",
				],
				result: "вы знаете свободное количество и единицу измерения материала.",
				tip: "«В резерве» — материал закреплён за объектом, но ещё не обязательно выдан. Материалы «под заказ» поступают через закупку; их не стоит считать обычным запасом.",
				image: shots.warehouse,
			},
			{
				title: "Как закрепить материал за объектом?",
				steps: [
					"Откройте **Склад → Резервации** и нажмите **Создать резервацию**.",
					"Выберите **Объект** и **Материал** из списков. Укажите нужное количество; проверьте единицу измерения.",
					"Нажмите **Зарезервировать**. Проверьте, что запись появилась во вкладке резерваций у нужного объекта.",
				],
				result:
					"материал закреплён за объектом. Фактическая выдача оформляется отдельно.",
				tip: "Если сервис сообщает о нехватке, не увеличивайте остаток вручную ради резерва. Передайте потребность ответственному за закупку.",
			},
			{
				title: "Как оформить выдачу по резерву?",
				steps: [
					"Во вкладке **Резервации** найдите запись нужного объекта и материала.",
					"Нажмите **Выдать** в этой строке. Укажите количество, которое фактически передаёте.",
					"Проверьте данные и нажмите **Выдать** в окне. Затем проверьте поля **Выдано** и **Остаток** в списке.",
				],
				result:
					"выдача учтена. При частичной выдаче оставшееся количество остаётся в резерве.",
				tip: "Не оформляйте ту же выдачу повторно через «Списание»: это другая операция.",
			},
			{
				title: "Как принять поступление или заказать недостающее?",
				steps: [
					"Если материал уже приехал без заявки в сервисе, откройте **Материалы → Поступление** и укажите фактически полученное количество.",
					"Для закупки откройте **Заявки на закупку** и форму новой заявки. Укажите поставщика и позиции: материал, количество и необходимые данные поставки.",
					"Создайте заявку. Если нужно согласование, дождитесь решения ответственного. Когда товар получен, откройте оприходование этой заявки, проверьте позиции и нажмите **Оприходовать**.",
				],
				result:
					"поставка или заявка отражена в сервисе. Создание заявки само по себе не увеличивает остаток.",
				tip: "Не оформляйте одну поставку и как отдельное поступление, и как оприходование заявки. Проверить операции можно во вкладке **История движений**.",
			},
		],
		related: [
			["/objects-and-blocks", "Карточка объекта"],
			["/troubleshooting", "Если не хватает доступа"],
		],
	},
	settings: {
		title: "Скачать отчёт и настроить график",
		intro:
			"Отчёты находятся в отдельном пункте меню. Настройки таблицы меняют работу графика вашей организации.",
		access,
		tasks: [
			{
				title: "Как скачать отчёт в Excel?",
				steps: [
					"В меню сервиса откройте **Отчёты**. Для первого раза оставьте вкладку **Общий отчёт**.",
					"Выберите **С месяца** и **По месяц**. Для отчёта за один месяц укажите один и тот же месяц в обоих полях.",
					"Если нужен отчёт по всем данным, оставьте фильтры пустыми. Нажмите **Скачать общий** внизу формы; при необходимости прокрутите страницу вниз.",
					"Откройте загрузки браузера — обычно значок стрелки вниз справа вверху. Нажмите на скачанный файл .xlsx, чтобы открыть его в Excel или другой программе для таблиц.",
				],
				result: "файл отчёта сохранён на компьютере.",
				tip: "Для другого вида отчёта выберите «По бригадам» или «Складская логистика». Если файла нет, проверьте сообщение об ошибке и доступ к отчётам.",
				image: shots.reports,
			},
			{
				title: "Как включить выходные или работу по часам?",
				steps: [
					"Откройте **Настройки → Настройки таблицы**. Нажмите **Изменить** внизу.",
					"Для показа субботы и воскресенья включите **Отображать выходные**. Для почасового графика включите **Учитывать время при планировании** и задайте начало и окончание рабочего дня.",
					"Нажмите **Сохранить** и вернитесь в график. Проверьте, как он теперь отображается.",
				],
				result: "график использует сохранённые настройки.",
				tip: "Эти параметры относятся к организации. Перед изменением согласуйте нужный режим с коллегами. Выбор между «Графиком» и «Просмотром графика» определяется доступом сотрудника.",
				image: shots.settings,
			},
		],
		related: [
			["/schedule", "Работа с графиком"],
			["/brigades", "Настройка бригад"],
		],
	},
	users: {
		title: "Пользователи и доступ",
		intro:
			"Каждому сотруднику нужна своя учётная запись. Выданные права определяют, какие разделы он видит и что может менять.",
		access,
		tasks: [
			{
				title: "Как добавить сотрудника?",
				steps: [
					"Откройте **Пользователи** в меню и нажмите **Добавить пользователя**.",
					"Заполните **Email**, **Полное имя**, **Установить пароль** и подтверждение пароля. В поле **Роль** выберите подходящий сотруднику вариант.",
					"Проверьте почту и выбранную роль, затем сохраните форму. Передайте сотруднику рабочую ссылку и данные для входа лично или через согласованный в компании канал.",
				],
				result:
					"сотрудник появился в списке пользователей и может войти под своими данными.",
				tip: "Не выбирайте роль руководителя только ради одной недостающей кнопки. Попросите администратора подобрать доступ под задачи сотрудника.",
				image: shots.users,
			},
			{
				title: "Почему у коллеги другие кнопки?",
				steps: [
					"Уточните, какую задачу нужно выполнить: например, посмотреть график, назначить работу или выдать материал.",
					"Попросите руководителя проверить вашу роль и доступ к этой задаче. Для склада также проверяют подключение модуля в тарифе.",
					"После изменения доступа обновите страницу. Если меню не изменилось, выйдите и войдите снова.",
				],
				result: "доступ проверен под вашу рабочую задачу.",
				tip: "Название должности само по себе не гарантирует доступ. Даже похожие роли могут иметь разные права.",
			},
		],
		related: [
			["/role-scenarios", "С чего начать по своей задаче"],
			["/getting-started", "Первый вход"],
		],
	},
	scenarios: {
		title: "Что мне делать в сервисе?",
		intro:
			"Выберите вашу задачу на сегодня. Не нужно изучать разделы, с которыми вы не работаете.",
		access,
		tasks: [
			{
				title: "Мне нужно только посмотреть план",
				steps: [
					"Откройте **Просмотр графика** или доступный вам **График**.",
					"Проверьте даты и найдите бригаду. Посмотрите название работы и объект.",
					"Если план не совпадает с договорённостями, сообщите ответственному: бригаду, дату и название работы.",
				],
				result: "вы знаете план и можете уточнить расхождения.",
			},
			{
				title: "Я распределяю работы между бригадами",
				steps: [
					"Проверьте, что нужный объект уже есть в списке.",
					"Откройте **График**: создайте работу или возьмите её из **В ожидании**.",
					"Назначьте дату и бригаду, затем проверьте итоговое положение блока.",
				],
				result: "команда видит запланированную работу.",
			},
			{
				title: "Я отвечаю за материалы",
				steps: [
					"Откройте **Склад → Материалы** и проверьте свободные остатки.",
					"В **Резервациях** проверьте материалы для объектов. Оформляйте **Выдачу** по фактически переданному количеству.",
					"Проверьте заявки на закупку. Полученный товар оприходуйте по соответствующей заявке.",
				],
				result: "остатки и операции отражают фактическое движение материалов.",
			},
			{
				title: "Я организую работу команды",
				steps: [
					"Проверьте учётные записи и доступ в **Пользователях**.",
					"Проверьте бригады и режим планирования в **Настройках**.",
					"Посмотрите график и при необходимости скачайте файл из **Отчётов**.",
				],
				result: "команда имеет нужный доступ, а вы видите общий план.",
			},
		],
		related: [
			["/schedule", "Инструкция по графику"],
			["/warehouse", "Инструкция по складу"],
			["/users-and-roles", "Пользователи"],
		],
	},
	troubleshooting: {
		title: "Если что-то не получается",
		intro:
			"Начните с похожей ситуации. Если шаги не помогли, напишите ответственному сотруднику вашей компании.",
		tasks: [
			{
				title: "Не получается войти",
				steps: [
					"Проверьте, что открыли рабочую ссылку вашей компании.",
					"Проверьте адрес в **Email**. В пароле проверьте язык клавиатуры и **Caps Lock** — клавишу больших букв. Значок глаза поможет увидеть опечатку.",
					"Если пароль забыт, нажмите **Забыли пароль?**, введите почту для входа и запросите восстановление. Проверьте входящие письма и папку «Спам».",
					"Если письма нет или доступа к почте больше нет, обратитесь к руководителю.",
				],
				result:
					"вы вошли или передали ответственному понятную причину проблемы.",
				tip: "Не отправляйте пароль и ссылку восстановления в общий чат.",
				image: shots.login,
			},
			{
				title: "В графике пусто или нет нужной работы",
				steps: [
					"Проверьте **Начальную дату** и **Конечную дату** над таблицей.",
					"Найдите нужную бригаду и прокрутите график до нужного дня.",
					"Если доступно, проверьте **В ожидании**. Уточните у менеджера, назначена ли работа и активна ли бригада.",
				],
				result: "вы проверили период, бригаду и неназначенные работы.",
			},
			{
				title: "Нет кнопки, раздела или написано «Доступ запрещен»",
				steps: [
					"Сообщите руководителю, какой раздел хотите открыть и что нужно сделать.",
					"Попросите проверить ваш доступ. Если нет склада — также подключение складского модуля.",
					"После исправления обновите страницу или войдите заново.",
				],
				result: "ответственный может проверить конкретную настройку доступа.",
			},
			{
				title: "Нажал «Сохранить», но ничего не изменилось",
				steps: [
					"Посмотрите, нет ли красного сообщения под полями. Заполните обязательные поля со звёздочкой.",
					"Если на кнопке идёт загрузка, дождитесь результата. Не нажимайте её много раз.",
					"При ошибке соединения запомните введённое. После восстановления связи сначала проверьте, появилась ли запись, и только потом повторяйте действие.",
				],
				result: "вы нашли причину или убедились, что запись уже сохранена.",
				tip: "Обновление страницы может стереть несохранённые поля формы.",
			},
			{
				title: "Как объяснить проблему ответственному?",
				steps: [
					"Напишите, где вы были и что нажали: например, «График → Создать».",
					"Укажите ожидаемый результат и что произошло вместо него. Добавьте дату, объект или бригаду, если это относится к проблеме.",
					"Приложите снимок с текстом ошибки. Перед отправкой убедитесь, что на нём нет пароля или лишних личных данных.",
				],
				result:
					"сообщение содержит достаточно информации, чтобы помочь без долгой переписки.",
			},
		],
		related: [
			["/getting-started", "Первые шаги"],
			["/schedule", "Как читать график"],
		],
	},
};
